import React, { useCallback, useEffect, useState } from 'react';
import { View, StyleSheet, FlatList, StatusBar, RefreshControl, Alert } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ResidentMobileHeaderWithMenu } from '@/components/residents/mobile/ResidentMobileHeaderWithMenu';
import { MobileScreenLayout } from '@/components/residents/mobile/MobileScreenLayout';
import { MobileNotificationCard } from '@/components/residents/notification/mobile/MobileNotificationCard';
import { NotificationEmptyState } from '@/components/residents/notification/mobile/NotificationEmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { LeaveFeedbackModal } from '@/components/residents/marketplace';
import { useResidentScreen } from '@/hooks/residents/useResidentScreen';
import { useNotifications, NotificationItem } from '@/hooks/common/useNotifications';
import { useMarketplaceFeedbackModal } from '@/hooks/common/useMarketplaceFeedbackModal';
import { useAppSelector, useAppDispatch } from '@/store';
import { fetchHomeData } from '@/store/slices/residents/homeSlice';
import { NOTIFICATION_COLORS } from '@/constants/residents/mobile/notificationColors';
import { strings } from '@/constants/residents/strings';

export const ResidentNotificationsScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();
  const { user } = useAppSelector((state) => state.auth);
  const [refreshing, setRefreshing] = useState(false);

  // Use notifications hook for data fetching and helpers
  const { 
    notifications, 
    isLoading, 
    reload,
    isUnread,
    markAsRead,
    deleteNotification,
    isMarketplaceFeedback,
  } = useNotifications();
  
  // Use consolidated marketplace feedback modal hook
  const feedbackModal = useMarketplaceFeedbackModal({ onSuccess: reload });

  // Use resident screen hook for navigation handling
  const {
    handleNavigate,
    handleLogoPress,
    handleSignOut,
    handleNotificationPress,
  } = useResidentScreen('ResidentNotifications', navigation);

  // Initial data load
  useEffect(() => {
    if (user?.propertyId) {
      dispatch(fetchHomeData(user.propertyId));
    }
  }, [dispatch, user?.propertyId]);

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await reload();
    } catch (error) {
      console.error('Failed to refresh notifications:', error);
    } finally {
      setRefreshing(false);
    }
  }, [reload]);

  // Handle notification expand - mark as read when user expands to view full content
  const handleNotificationExpand = useCallback(async (notification: NotificationItem) => {
    try {
      await markAsRead(notification);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, [markAsRead]);

  // Handle delete notification
  const handleDeleteNotification = useCallback((notification: NotificationItem) => {
    // Don't allow deleting marketplace feedback notifications
    if (isMarketplaceFeedback(notification)) return;
    
    Alert.alert(
      strings.notifications.mobile.delete.title,
      strings.notifications.mobile.delete.message,
      [
        {
          text: strings.notifications.mobile.delete.cancel,
          style: 'cancel',
        },
        {
          text: strings.notifications.mobile.delete.confirm,
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteNotification(notification.id);
            } catch (error) {
              console.error('Failed to delete notification:', error);
              Alert.alert(strings.alert.error, 'Failed to delete notification. Please try again.');
            }
          },
        },
      ],
      { cancelable: true }
    );
  }, [deleteNotification, isMarketplaceFeedback]);

  // Handle marketplace feedback notification press - delegate to modal hook
  const handleMarketplaceFeedbackPress = useCallback((notification: NotificationItem) => {
    feedbackModal.openModal(notification);
  }, [feedbackModal]);

  // Handle cork board warning notification press - navigate to thread and mark as read
  const handleCorkBoardWarningPress = useCallback(async (notification: NotificationItem) => {
    try {
      await markAsRead(notification);
    } catch {}

    const threadId = notification.corkBoardNotification?.metadata?.threadId;
    if (threadId) {
      navigation.navigate('ThreadDetail', { threadId: String(threadId) });
    }
  }, [markAsRead, navigation]);

  // Render notification card
  const renderItem = useCallback(({ item }: { item: NotificationItem }) => (
    <MobileNotificationCard
      notification={item}
      isUnread={isUnread(item)}
      onExpand={handleNotificationExpand}
      onDelete={handleDeleteNotification}
      onMarketplaceFeedbackPress={handleMarketplaceFeedbackPress}
      onCorkBoardWarningPress={handleCorkBoardWarningPress}
    />
  ), [isUnread, handleNotificationExpand, handleDeleteNotification, handleMarketplaceFeedbackPress, handleCorkBoardWarningPress]);

  // Key extractor
  const keyExtractor = useCallback((item: NotificationItem) => `notification-${item.id}`, []);

  // Item separator
  const ItemSeparator = useCallback(() => <View style={styles.separator} />, []);

  // Render empty state - uses default strings from NotificationEmptyState
  const renderEmptyState = useCallback(() => (
    <NotificationEmptyState />
  ), []);

  // Show loading state
  const showLoading = isLoading && (!notifications || notifications.length === 0);

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" />
      
      {/* Header with Navigation Menu */}
      <ResidentMobileHeaderWithMenu
        onLogoPress={handleLogoPress}
        onSignOut={handleSignOut}
        onNavigate={handleNavigate}
        onNotificationPress={handleNotificationPress}
        currentPath="/notifications"
        currentTitle={strings.notifications.mobile.screenTitle}
        showNotification={true}
      />

      {/* Content with Gradient Border Frame */}
      <MobileScreenLayout
        backgroundColor={NOTIFICATION_COLORS.screenBackground}
        scrollable={false}
        contentPadding={0}
        contentGap={0}
      >
        {showLoading ? (
          <View style={styles.loadingContainer}>
            <LoadingSpinner message={strings.notifications.mobile.loading} variant="admin" />
          </View>
        ) : (
          <FlatList
            data={notifications || []}
            keyExtractor={keyExtractor}
            renderItem={renderItem}
            ItemSeparatorComponent={ItemSeparator}
            contentContainerStyle={[
              styles.listContent,
              { paddingBottom: 40 + insets.bottom },
              (!notifications || notifications.length === 0) && styles.listContentEmpty,
            ]}
            ListEmptyComponent={renderEmptyState}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={handleRefresh}
                tintColor={NOTIFICATION_COLORS.refreshTint}
              />
            }
            showsVerticalScrollIndicator={false}
          />
        )}
      </MobileScreenLayout>

      {/* Marketplace Feedback Modal */}
      <LeaveFeedbackModal
        visible={feedbackModal.isVisible}
        feedbackRequest={feedbackModal.feedbackRequest}
        onClose={feedbackModal.closeModal}
        onSubmit={feedbackModal.submitFeedback}
        isSubmitting={feedbackModal.isSubmitting}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: NOTIFICATION_COLORS.cardBackground,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    paddingTop: 20,
    paddingBottom: 40,
    paddingHorizontal: 20,
    gap: 16,
  },
  listContentEmpty: {
    flexGrow: 1,
  },
  separator: {
    height: 16,
  },
});

export default ResidentNotificationsScreen;
